import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import { ArrowLeftIcon, CarIcon, ExpandIcon, MaximizeIcon, MinimizeIcon, MonitorIcon, RefreshCwIcon, ShrinkIcon, SkullIcon, UserIcon } from 'lucide-react';
import { useBackendApi } from '@/hooks/fetch';
import { cn } from '@/lib/utils';
import type { CrashMapLivePlayer } from '@shared/vantaCrashMapTypes';
import { ModerationBar } from './Moderation';

type ScreenShot = { id: number; ts: number | null; img: string | null; err: string | null };
type ScreenResp = { ts: number; screen: ScreenShot } | { error: string };
type ScreensResp = { ts: number; screens: ScreenShot[] } | { error: string };

const FOCUS_REFRESH_MS = 2000;
const WALL_REFRESH_MS = 5000;

const ago = (ts: number | null, now: number) => {
    if (!ts) return 'waiting…';
    const s = Math.max(0, Math.round((now - ts) / 1000));
    return s <= 1 ? 'live' : `${s}s ago`;
};

function useNow(intervalMs = 1000) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), intervalMs);
        return () => clearInterval(t);
    }, [intervalMs]);
    return now;
}

/**
 * Cadre d'écran agrandissable : grand format dans la page (Échap pour revenir) ou plein écran (double-clic).
 */
function ExpandableFrame({ children, title, className }: { children: React.ReactNode; title?: string; className?: string }) {
    const ref = useRef<HTMLDivElement>(null);
    const [expanded, setExpanded] = useState(false);
    const [fullscreen, setFullscreen] = useState(false);

    useEffect(() => {
        const onFs = () => setFullscreen(document.fullscreenElement === ref.current);
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setExpanded(false); };
        document.addEventListener('fullscreenchange', onFs);
        window.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('fullscreenchange', onFs);
            window.removeEventListener('keydown', onKey);
        };
    }, []);

    const toggleFullscreen = () => {
        if (document.fullscreenElement) document.exitFullscreen().catch(() => { });
        else ref.current?.requestFullscreen().catch(() => { });
    };

    return (
        <>
            {expanded && <div className="fixed inset-0 z-40 bg-black/80" onClick={() => setExpanded(false)} />}
            <div
                ref={ref}
                onDoubleClick={toggleFullscreen}
                className={cn(
                    'group relative overflow-hidden bg-black',
                    expanded
                        ? 'fixed left-1/2 top-1/2 z-50 w-[min(92vw,calc(88vh*16/9))] aspect-video -translate-x-1/2 -translate-y-1/2 rounded-lg border shadow-2xl'
                        : 'aspect-video w-full rounded-md border',
                    fullscreen && 'rounded-none border-0',
                    className,
                )}
            >
                {children}
                {expanded && title && (
                    <span className="absolute left-2 top-2 rounded bg-black/70 px-2 py-0.5 text-xs text-white">{title}</span>
                )}
                <div className="absolute bottom-1.5 right-1.5 flex gap-1 opacity-70 group-hover:opacity-100">
                    {!fullscreen && (
                        <button type="button" title={expanded ? 'Shrink (Esc)' : 'Expand'} onClick={() => setExpanded((v) => !v)}
                            className="rounded bg-black/70 p-1.5 text-white hover:bg-black">
                            {expanded ? <ShrinkIcon className="size-4" /> : <ExpandIcon className="size-4" />}
                        </button>
                    )}
                    <button type="button" title={fullscreen ? 'Exit fullscreen' : 'Fullscreen (double-click)'} onClick={toggleFullscreen}
                        className="rounded bg-black/70 p-1.5 text-white hover:bg-black">
                        {fullscreen ? <MinimizeIcon className="size-4" /> : <MaximizeIcon className="size-4" />}
                    </button>
                </div>
            </div>
        </>
    );
}

/**
 * Image d'écran avec état (en attente, erreur, en direct).
 */
function ScreenImage({ shot, now, className, title, expandable }: { shot?: ScreenShot; now: number; className?: string; title?: string; expandable?: boolean }) {
    const stale = shot?.ts ? now - shot.ts > 12_000 : true;
    const content = (
        <>
            {shot?.img ? (
                <img src={shot.img} alt="" className={cn('h-full w-full object-cover', stale && 'opacity-50')} draggable={false} />
            ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-xs text-muted-foreground">
                    <MonitorIcon className="size-6 animate-pulse" />
                    {shot?.err ? `Capture failed: ${shot.err}` : 'Waiting for the first capture…'}
                </div>
            )}
            {shot?.img && (
                <span className={cn(
                    'absolute right-1.5 top-1.5 rounded px-1.5 py-px text-[10px] font-bold uppercase',
                    stale ? 'bg-muted text-muted-foreground' : 'bg-destructive text-destructive-foreground',
                )}>
                    {stale ? ago(shot.ts, now) : 'live'}
                </span>
            )}
        </>
    );
    //pas de composant défini pendant le rendu : l'image n'est pas recréée à chaque rafraîchissement
    return expandable ? (
        <ExpandableFrame title={title} className={className}>{content}</ExpandableFrame>
    ) : (
        <div className={cn('relative aspect-video w-full overflow-hidden rounded-md border bg-black', className)}>{content}</div>
    );
}

type StreamStateResp = { sid: string; state: string; offer?: string; error?: string } | { error: string };
const STUN = [{ urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] }];

const waitIce = (pc: RTCPeerConnection) => new Promise<void>((resolve) => {
    if (pc.iceGatheringState === 'complete') return resolve();
    const check = () => { if (pc.iceGatheringState === 'complete') { pc.removeEventListener('icegatheringstatechange', check); resolve(); } };
    pc.addEventListener('icegatheringstatechange', check);
    setTimeout(resolve, 4000);
});

/**
 * Vidéo en direct (WebRTC pair-à-pair avec le jeu du joueur, négociation via txAdmin).
 */
function LiveVideo({ playerId, title, onFallback }: { playerId: number; title?: string; onFallback: () => void }) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [status, setStatus] = useState('Starting the stream…');
    const [failed, setFailed] = useState<string | null>(null);
    const [attempt, setAttempt] = useState(0);
    const startApi = useBackendApi<{ sid: string } | { error: string }>({ method: 'POST', path: '/vanta/crashmap/stream-start' });
    const stateApi = useBackendApi<StreamStateResp>({ method: 'POST', path: '/vanta/crashmap/stream-state' });
    const answerApi = useBackendApi<{ success: true } | { error: string }>({ method: 'POST', path: '/vanta/crashmap/stream-answer' });
    const stopApi = useBackendApi({ method: 'POST', path: '/vanta/crashmap/stream-stop' });

    useEffect(() => {
        let cancelled = false;
        let sid: string | null = null;
        let pc: RTCPeerConnection | null = null;
        let timer: ReturnType<typeof setTimeout> | undefined;
        setFailed(null);
        setStatus('Starting the stream…');

        const fail = (msg: string) => {
            if (cancelled) return;
            setFailed(msg);
            pc?.close();
        };
        const poll = async () => {
            if (cancelled || !sid) return;
            const st = await stateApi({ data: { sid } }).catch(() => null);
            if (cancelled) return;
            if (!st || 'error' in st && !('state' in st)) return fail((st as any)?.error ?? 'No answer from txAdmin.');
            const s = st as Exclude<StreamStateResp, { error: string }>;
            if (s.state === 'ended' || s.state === 'error') return fail(s.error ? `Stream ended: ${s.error}` : 'Stream ended.');
            if (s.state === 'offer' && s.offer && !pc) {
                setStatus('Connecting to the player…');
                pc = new RTCPeerConnection({ iceServers: STUN });
                pc.addEventListener('track', (ev) => {
                    if (videoRef.current) {
                        videoRef.current.srcObject = ev.streams[0] ?? new MediaStream([ev.track]);
                        videoRef.current.play().catch(() => { });
                    }
                });
                pc.addEventListener('connectionstatechange', () => {
                    if (!pc) return;
                    if (pc.connectionState === 'connected') setStatus('');
                    if (pc.connectionState === 'failed') fail('Direct connection failed (NAT/firewall). Use screenshots instead.');
                });
                await pc.setRemoteDescription({ type: 'offer', sdp: s.offer });
                const answer = await pc.createAnswer();
                await pc.setLocalDescription(answer);
                await waitIce(pc);
                if (cancelled) return;
                const resp = await answerApi({ data: { sid, sdp: pc.localDescription?.sdp ?? answer.sdp } });
                if (resp && 'error' in resp) return fail(resp.error);
            } else if (s.state === 'requested') {
                setStatus('Waiting for the player\'s game…');
            }
            timer = setTimeout(poll, pc ? 3000 : 1000);
        };

        (async () => {
            const resp = await startApi({ data: { id: playerId } }).catch(() => null);
            if (cancelled) return;
            if (!resp || 'error' in resp) return fail((resp as any)?.error ?? 'Could not start the stream.');
            sid = resp.sid;
            poll();
        })();

        return () => {
            cancelled = true;
            clearTimeout(timer);
            pc?.close();
            if (sid) stopApi({ data: { sid } }).catch(() => { });
        };
    }, [playerId, attempt]);

    return (
        <ExpandableFrame title={title}>
            <video ref={videoRef} className="h-full w-full object-contain" muted playsInline autoPlay />
            {!failed && status && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-xs text-muted-foreground">
                    <MonitorIcon className="size-6 animate-pulse" />{status}
                </div>
            )}
            {failed && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-3 text-center text-xs text-destructive">
                    {failed}
                    <div className="flex gap-2">
                        <button type="button" className="flex items-center gap-1 rounded border px-2 py-1 text-foreground hover:bg-muted" onClick={() => setAttempt((a) => a + 1)}>
                            <RefreshCwIcon className="size-3" />Retry
                        </button>
                        <button type="button" className="rounded border px-2 py-1 text-foreground hover:bg-muted" onClick={onFallback}>Screenshots</button>
                    </div>
                </div>
            )}
            {!failed && !status && (
                <span className="absolute right-1.5 top-1.5 rounded bg-destructive px-1.5 py-px text-[10px] font-bold uppercase text-destructive-foreground">live</span>
            )}
        </ExpandableFrame>
    );
}

/**
 * Panneau latéral : écran en direct d'un joueur (capture toutes les ~2 s tant que le panneau est ouvert).
 */
export function ScreenPanel({ player, serverMutex, onBack, onOpenInfo }: {
    player: CrashMapLivePlayer;
    serverMutex: string | null;
    onBack: () => void;
    onOpenInfo: (p: CrashMapLivePlayer) => void;
}) {
    const now = useNow();
    const [view, setView] = useState<'video' | 'shots'>('video');
    const screenApi = useBackendApi<ScreenResp>({ method: 'GET', path: '/vanta/crashmap/screen' });
    const { data, error } = useSWR(view === 'shots' ? `/vanta/crashmap/screen?id=${player.id}` : null, async () => {
        const resp = await screenApi({ queryParams: { id: player.id } });
        if (!resp) throw new Error('empty_response');
        if ('error' in resp) throw new Error(resp.error);
        return resp;
    }, { refreshInterval: FOCUS_REFRESH_MS, revalidateOnFocus: false, keepPreviousData: true, dedupingInterval: 500 });

    return (
        <div className="flex flex-col gap-3 p-4">
            <button type="button" onClick={onBack} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground w-fit">
                <ArrowLeftIcon className="size-3.5" />All players
            </button>
            <div className="flex items-center gap-2 min-w-0">
                <span className="font-mono text-xs text-muted-foreground">#{player.id}</span>
                <h3 className="truncate font-semibold">{player.name}</h3>
                {player.vehicle && <CarIcon className="size-3.5 shrink-0 text-muted-foreground" />}
                {!player.alive && <SkullIcon className="size-3.5 shrink-0 text-destructive" />}
            </div>
            <div className="flex rounded-md border overflow-hidden text-xs w-fit">
                {([['video', 'Live video'], ['shots', 'Screenshots']] as const).map(([v, label], i) => (
                    <button
                        key={v}
                        type="button"
                        className={cn('px-2.5 py-1', i > 0 && 'border-l', view === v ? 'bg-secondary text-secondary-foreground' : 'text-muted-foreground hover:bg-muted')}
                        onClick={() => setView(v)}
                    >{label}</button>
                ))}
            </div>
            {view === 'video' ? (
                <LiveVideo playerId={player.id} title={`#${player.id} ${player.name}`} onFallback={() => setView('shots')} />
            ) : error ? (
                <p className="rounded-md border border-destructive/50 p-3 text-sm text-destructive">{(error as Error).message}</p>
            ) : (
                <ScreenImage shot={data?.screen} now={now} expandable title={`#${player.id} ${player.name}`} />
            )}
            <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="rounded-md border p-2"><div className="text-muted-foreground">X</div><div className="font-mono">{player.x.toFixed(0)}</div></div>
                <div className="rounded-md border p-2"><div className="text-muted-foreground">Y</div><div className="font-mono">{player.y.toFixed(0)}</div></div>
                <div className="rounded-md border p-2"><div className="text-muted-foreground">Z</div><div className="font-mono">{player.z.toFixed(0)}</div></div>
            </div>
            <button
                type="button"
                onClick={() => onOpenInfo(player)}
                className="flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-muted"
            >
                <UserIcon className="size-4" />Player info
            </button>
            <ModerationBar player={player} serverMutex={serverMutex} />
            <p className="text-[11px] leading-snug text-muted-foreground">
                Live video is sent peer-to-peer from the player's game (WebRTC, 720p ~10 fps) while this panel is open; screenshots are small captures taken by the game (screenshot-basic as fallback). Viewing is logged in the Action Log.
            </p>
        </div>
    );
}

/**
 * Mur d'écrans : tous les joueurs connectés, une petite capture (480 px) toutes les ~10 s.
 */
export function ScreensWall({ players, onSelect }: {
    players: CrashMapLivePlayer[];
    onSelect: (p: CrashMapLivePlayer) => void;
}) {
    const now = useNow();
    const ids = players.map((p) => p.id).sort((a, b) => a - b);
    const idsKey = ids.join(',');
    const screensApi = useBackendApi<ScreensResp>({ method: 'GET', path: '/vanta/crashmap/screens' });
    const { data, error } = useSWR(ids.length ? `/vanta/crashmap/screens?ids=${idsKey}` : null, async () => {
        const resp = await screensApi({ queryParams: { ids: idsKey } });
        if (!resp) throw new Error('empty_response');
        if ('error' in resp) throw new Error(resp.error);
        return resp;
    }, { refreshInterval: WALL_REFRESH_MS, revalidateOnFocus: false, keepPreviousData: true });

    const byId = new Map((data?.screens ?? []).map((s) => [s.id, s]));
    if (error) {
        return <p className="m-4 rounded-md border border-destructive/50 p-3 text-sm text-destructive">{(error as Error).message}</p>;
    }
    if (!players.length) {
        return <p className="m-auto p-8 text-sm text-muted-foreground">No player online.</p>;
    }
    return (
        <div className="grid gap-3 p-3 overflow-y-auto grid-cols-[repeat(auto-fill,minmax(280px,1fr))] content-start">
            {[...players].sort((a, b) => a.id - b.id).map((p) => (
                <button
                    key={p.id}
                    type="button"
                    onClick={() => onSelect(p)}
                    className="group flex flex-col gap-1.5 rounded-lg border bg-background/40 p-2 text-left hover:border-primary"
                >
                    <ScreenImage shot={byId.get(p.id)} now={now} />
                    <div className="flex items-center gap-2 min-w-0 text-sm">
                        <span className="font-mono text-xs text-muted-foreground">#{p.id}</span>
                        <span className="truncate">{p.name}</span>
                        {p.vehicle && <CarIcon className="size-3.5 shrink-0 text-muted-foreground" />}
                        {!p.alive && <SkullIcon className="size-3.5 shrink-0 text-destructive" />}
                    </div>
                </button>
            ))}
        </div>
    );
}
